import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, X, Loader2, ChevronLeft, ChevronRight, Coffee } from 'lucide-react';
import { useCourtBooking, CourtSlot } from '@/hooks/useCourtBooking';

interface ParticipantCourtBookingProps {
  eventId: string;
  selectedWeek: number;
  matchDuration?: number;
  weekDateLabel?: string;
  totalWeeks?: number;
  weekLabels?: string[];
  restWeeks?: number[];
  weekStartDates?: Date[];
  onWeekChange?: (week: number) => void;
}

const dayTranslations: Record<string, string> = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo',
  lunes: 'Lunes',
  martes: 'Martes',
  miércoles: 'Miércoles',
  miercoles: 'Miércoles',
  jueves: 'Jueves',
  viernes: 'Viernes',
  sábado: 'Sábado',
  sabado: 'Sábado',
  domingo: 'Domingo',
};

const allDays = [
  { key: 'lunes', label: 'L' },
  { key: 'martes', label: 'M' },
  { key: 'miércoles', label: 'Mi' },
  { key: 'jueves', label: 'J' },
  { key: 'viernes', label: 'V' },
  { key: 'sábado', label: 'S' },
  { key: 'domingo', label: 'D' },
];

const normalizeDayKey = (day: string): string => {
  const map: Record<string, string> = {
    monday: 'lunes', tuesday: 'martes', wednesday: 'miércoles',
    thursday: 'jueves', friday: 'viernes', saturday: 'sábado', sunday: 'domingo',
    lunes: 'lunes', martes: 'martes', miércoles: 'miércoles', miercoles: 'miércoles',
    jueves: 'jueves', viernes: 'viernes', sábado: 'sábado', sabado: 'sábado', domingo: 'domingo',
  };
  return map[day.toLowerCase()] || day.toLowerCase();
};

const formatTime = (time: string) => {
  // "09:00:00" -> "09:00"
  return time.substring(0, 5);
};

const translateDay = (day: string) => {
  return dayTranslations[day.toLowerCase()] || day;
};

const ParticipantCourtBooking: React.FC<ParticipantCourtBookingProps> = ({
  eventId,
  selectedWeek,
  matchDuration = 90,
  weekDateLabel,
  totalWeeks,
  weekLabels,
  restWeeks = [],
  weekStartDates,
  onWeekChange,
}) => {
  // Calculate adjusted total weeks (regular + rest weeks)
  const adjustedTotalWeeks = (totalWeeks || 0) + restWeeks.length;

  const isInternalChange = useRef(false);
  const [internalWeek, setInternalWeek] = useState(selectedWeek);

  // Sync with parent when selectedWeek changes (only if not from internal navigation)
  useEffect(() => {
    if (!isInternalChange.current) {
      setInternalWeek(selectedWeek);
    }
    isInternalChange.current = false;
  }, [selectedWeek]);

  // Notify parent when internalWeek changes
  useEffect(() => {
    isInternalChange.current = true;
    onWeekChange?.(internalWeek);
  }, [internalWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCurrentRestWeek = restWeeks.includes(internalWeek);
  const maxWeek = adjustedTotalWeeks > 0 ? adjustedTotalWeeks : (totalWeeks || internalWeek);
  const currentWeekLabel = weekLabels && weekLabels[internalWeek - 1] ? weekLabels[internalWeek - 1] : weekDateLabel;

  const currentWeekStartDate = weekStartDates?.[internalWeek - 1] || undefined;

  const {
    slots,
    userBooking,
    hasBookingThisWeek,
    isLoading,
    isBooking,
    isCancelling,
    bookCourt,
    cancelBooking,
    hasRegistration,
  } = useCourtBooking(eventId, internalWeek, matchDuration, currentWeekStartDate);

  const daysWithSlots = useMemo(() => {
    const set = new Set<string>();
    slots.forEach(s => set.add(normalizeDayKey(s.dayOfWeek)));
    return set;
  }, [slots]);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    const firstAvailable = allDays.find(d => daysWithSlots.has(d.key));
    setSelectedDay(firstAvailable?.key || null);
  }, [daysWithSlots]);

  const hasNoSlots = slots.length === 0;

  const filteredSlots = useMemo(() => {
    return selectedDay
      ? slots.filter(s => normalizeDayKey(s.dayOfWeek) === selectedDay)
      : slots;
  }, [slots, selectedDay]);

  // Group slots by time range
  const groupedByTime = useMemo(() => {
    const groups = new Map<string, CourtSlot[]>();
    filteredSlots.forEach(slot => {
      const key = `${slot.startTime}-${slot.endTime}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(slot);
    });
    return Array.from(groups.entries()).map(([, groupSlots]) => {
      // Deduplicate by court name - keep booked version if exists
      const uniqueCourts = new Map<string, CourtSlot>();
      groupSlots.forEach(slot => {
        const existing = uniqueCourts.get(slot.courtName);
        if (!existing || slot.isBooked) {
          uniqueCourts.set(slot.courtName, slot);
        }
      });
      return {
        startTime: groupSlots[0].startTime,
        endTime: groupSlots[0].endTime,
        slots: Array.from(uniqueCourts.values()).sort((a, b) => a.courtNumber - b.courtNumber),
      };
    });
  }, [filteredSlots]);

  const hasAnySlots = filteredSlots.length > 0;
  const hasAvailableSlots = filteredSlots.some(s => !s.isBooked);

  if (isLoading) {
    return (
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardContent className="p-6 flex items-center justify-center gap-2 text-blue-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Cargando disponibilidad...</span>
        </CardContent>
      </Card>
    );
  }

  const getDayNameFromDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay();
    const names = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
    return names[dayIndex];
  };

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 shadow-md">
      <CardContent className="p-4 md:p-6 space-y-4">
        {/* Header with week selector */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-green-600" />
            <h3 className="font-bold text-green-900 text-base md:text-lg">
              Reservar Cancha
            </h3>
          </div>
          <Badge variant="outline" className="border-green-300 text-green-700 text-xs">
            1 reserva por semana
          </Badge>
        </div>

        {/* Week navigator */}
        {maxWeek > 1 ? (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-green-700 hover:bg-green-100"
              onClick={() => setInternalWeek(w => Math.max(1, w - 1))}
              disabled={internalWeek <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center min-w-[140px]">
              <p className="font-semibold text-green-900 text-sm">
                Semana {internalWeek}
                {isCurrentRestWeek && <span className="text-amber-600 ml-1">· Descanso</span>}
              </p>
              {currentWeekLabel && (
                <p className="text-xs text-green-600">{currentWeekLabel}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-green-700 hover:bg-green-100"
              onClick={() => setInternalWeek(w => Math.min(maxWeek, w + 1))}
              disabled={internalWeek >= maxWeek}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            {currentWeekLabel && (
              <p className="text-sm text-green-700">Semana {internalWeek} · {currentWeekLabel}</p>
            )}
          </>
        )}

        {/* Rest week banner */}
        {isCurrentRestWeek && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center space-y-1">
            <div className="flex items-center justify-center gap-2 text-amber-700">
              <Coffee className="h-5 w-5" />
              <span className="font-semibold text-sm">Semana de descanso</span>
            </div>
            <p className="text-xs text-amber-600">No hay partidos programados esta semana</p>
          </div>
        )}

        {/* Day selector - hidden during rest weeks */}
        {!isCurrentRestWeek && (
        <div className="flex items-center justify-center gap-2">
          {allDays.map(({ key, label }) => {
            const hasSlotsForDay = !hasNoSlots && daysWithSlots.has(key);
            const isSelected = selectedDay === key;
            return (
              <button
                key={key}
                disabled={!hasSlotsForDay}
                onClick={() => setSelectedDay(key)}
                className={`w-9 h-9 rounded-full text-xs font-semibold transition-colors
                  ${isSelected && hasSlotsForDay
                    ? 'bg-green-600 text-white shadow-md'
                    : hasSlotsForDay
                      ? 'border border-gray-300 text-gray-700 hover:border-green-400 hover:text-green-700 bg-white'
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        )}

        {/* No slots message - hidden during rest weeks */}
        {!isCurrentRestWeek && hasNoSlots && !isLoading && (
          <p className="text-sm text-gray-400 text-center py-3">
            Sin horarios establecidos para esta semana
          </p>
        )}

        {/* User's current booking */}
        {!isCurrentRestWeek && userBooking && (
          <div className="bg-green-100 border border-green-300 rounded-lg p-3 md:p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-green-600 text-white text-xs">Tu reserva</Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-green-800">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {userBooking.court_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {getDayNameFromDate(userBooking.booking_date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatTime(userBooking.start_time)} - {formatTime(userBooking.end_time)}
                  </span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => cancelBooking(userBooking.id)}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <X className="h-3 w-3 mr-1" />
                )}
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* No available slots message */}
        {!isCurrentRestWeek && !hasBookingThisWeek && hasAnySlots && !hasAvailableSlots && (
          <p className="text-sm text-gray-500 text-center py-2">
            Todos los horarios están reservados
          </p>
        )}

        {/* Grouped slots by time */}
        {!isCurrentRestWeek && groupedByTime.length > 0 && (
          <div className="space-y-3">
            {!hasBookingThisWeek && hasAvailableSlots && (
              <p className="text-xs text-green-700 font-medium">Horarios disponibles:</p>
            )}
            {groupedByTime.map(({ startTime, endTime, slots: groupSlots }) => {
              // Skip groups where all slots are booked by current user (shown in booking card)
              const relevantSlots = groupSlots.filter(s => !s.bookedByCurrentUser);
              if (relevantSlots.length === 0) return null;

              return (
                <div
                  key={`${startTime}-${endTime}`}
                  className="bg-white rounded-lg border border-green-200 p-3 space-y-2"
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium text-green-800">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{formatTime(startTime)} - {formatTime(endTime)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {relevantSlots.map((slot, idx) => (
                      <div
                        key={`${slot.courtName}-${idx}`}
                        className={`rounded-lg border p-2.5 text-center transition-all ${
                          slot.isBooked
                            ? 'bg-gray-50 border-gray-200 opacity-50'
                            : 'bg-green-50/50 border-green-200 hover:shadow-sm'
                        }`}
                      >
                        <p className={`text-xs font-semibold mb-1.5 ${
                          slot.isBooked ? 'text-gray-400' : 'text-gray-800'
                        }`}>
                          {slot.courtName}
                        </p>
                        {slot.isBooked ? (
                          <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
                            Reservado
                          </Badge>
                        ) : hasBookingThisWeek ? (
                          <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-gray-400 border-gray-200">
                            Disponible
                          </Badge>
                        ) : !hasRegistration ? (
                          <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-gray-400 border-gray-200">
                            Inscríbete
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-[10px] h-6 px-3 w-full"
                            onClick={() => bookCourt(slot)}
                            disabled={isBooking}
                          >
                            {isBooking ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              'Reservar'
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantCourtBooking;
